import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Mail, ArrowRight, Loader2, Info, Clock } from 'lucide-react';
import { toast } from 'sonner';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Domaines autorisés pour l'accès VIPPLUS
  const allowedDomains = ['@vipplus.com', '@mana.fr', '@manadvise.fr'];

  const isEmailAllowed = (email) => {
    return allowedDomains.some(domain => email.toLowerCase().endsWith(domain));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    // Vérification du domaine autorisé
    if (!isEmailAllowed(email)) {
      toast.error(
        'Vous ne faites pas partie de l\'organisation VIPPLUS. Seuls les collaborateurs avec une adresse @vipplus.com peuvent accéder à cette plateforme.',
        { duration: 6000 }
      );
      return;
    }

    setLoading(true);
    const result = await login(email);
    setLoading(false);

    if (result.success) {
      toast.success('Connexion réussie !');
      navigate('/vipplus');
    } else {
      toast.error('Erreur de connexion. Veuillez réessayer.');
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#571925] items-center justify-center p-12">
        <div className="text-center text-white max-w-md">
          <img 
            src="/VIP.png" 
            alt="VIP Plus" 
            className="h-24 w-auto mx-auto mb-8"
          />
          <h1 className="text-4xl font-bold mb-4 font-['Outfit']">VIP Plus Academy</h1>
          <p className="text-xl text-white/80 mb-6">Plateforme de formation interactive aux modules Salesforce</p>
          <div className="grid grid-cols-3 gap-4 mt-8">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold">9</div>
              <div className="text-sm text-white/70">Parcours</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold">45</div>
              <div className="text-sm text-white/70">Étapes</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold">100%</div>
              <div className="text-sm text-white/70">Interactif</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#f2f2f2]">
        <Card className="w-full max-w-md shadow-lg border-0">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden mb-6">
              <img 
                src="/VIP.png" 
                alt="VIP Plus" 
                className="h-16 w-auto mx-auto"
              />
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <CardTitle className="text-2xl font-bold text-[#571925] font-['Outfit']">
                Bienvenue
              </CardTitle>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-blue-500" />
                      Information importante
                    </DialogTitle>
                    <DialogDescription className="text-left pt-2">
                      Après une période d'inactivité, il est normal que la connexion prenne 
                      plus d'une minute lors du premier accès.
                      <br /><br />
                      <strong>Pas d'inquiétude !</strong> Nos serveurs se mettent automatiquement 
                      en veille pour optimiser les performances. Le temps de chargement initial 
                      est tout à fait normal.
                      <br /><br />
                      <span className="text-sm text-gray-500">
                        Une fois connecté, la navigation sera fluide.
                      </span>
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            </div>
            <CardDescription className="text-gray-600">
              Connectez-vous avec votre adresse email professionnelle
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-[#1a1a1a]">
                  Adresse email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre.email@vipplus.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 border-gray-200 focus:border-[#cc5759] focus:ring-[#cc5759]"
                    data-testid="email-input"
                    required
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-[#ff9a52] hover:bg-[#e08540] text-white rounded-full font-semibold transition-all"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  <>
                    Accéder à la plateforme
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-500">
                En vous connectant, vous acceptez les conditions d'utilisation de la plateforme VIP Plus Academy.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
